// Local intent-based chatbot for Digi Savvy — runs entirely client-side.
// No backend dependency. Matches user input against keyword intents and
// returns a contextual reply. Falls back to WhatsApp handoff for anything
// it can't confidently answer.

const WA = "https://wa.me/919818825449";

const INTENTS = [
  {
    name: "pricing",
    keywords: ["price", "pricing", "cost", "rate", "rates", "fees", "fee", "package", "retainer", "monthly", "budget", "kitna", "kitne", "kharcha"],
    reply: "Our retainers typically start from about 1.5 lakhs per month for boutique projects and scale based on ad-spend and scope. We share a transparent rate card after a quick inventory audit. Want me to set that up on WhatsApp?",
  },
  {
    name: "rera",
    keywords: ["rera", "compliant", "compliance", "legal", "disclaimer"],
    reply: "Yes — every visual we ship is reviewed against state RERA guidelines for Delhi, Gurugram, Noida, UP, Haryana, Mumbai and Rajasthan. We include the required disclaimers, project IDs and possession-date language so your creative is safe and sellable.",
  },
  {
    name: "nri",
    keywords: ["nri", "dubai", "singapore", "uk", "london", "canada", "toronto", "overseas", "abroad", "international", "expat"],
    reply: "We run dedicated NRI funnels in Dubai, Singapore, London, Toronto and San Francisco — geo-targeted ads, in-language landing pages, WhatsApp Business and time-zone-aware sales follow-up. NRI is one of our strongest verticals.",
  },
  {
    name: "location",
    keywords: ["office", "location", "address", "where", "visit", "based", "studio", "delhi office"],
    reply: "Our studio is at Sector 3, Rohini, Delhi 110085. You're welcome to drop by — or we can come to your site anywhere in India within 72 hours.",
  },
  {
    name: "services",
    keywords: ["service", "services", "what do you do", "offerings", "kya karte", "lever", "levers"],
    reply: "We run 13 growth levers under one roof — performance marketing, lead generation, social media, SEO, website development, branding & creative, video production, 3D walkthroughs, WhatsApp marketing, PR & influencer, outdoor advertising, event marketing and full 360° campaigns.",
  },
  {
    name: "leadgen",
    keywords: ["lead", "leads", "buyers", "site visit", "site-visit", "qualified", "cpl"],
    reply: "Our lead-generation engines are tuned for site-visit-ready buyers — not vanity clicks. CPLs typically drop week-on-week, with leads routed to your sales team in under 60 seconds via WhatsApp and CRM automation.",
  },
  {
    name: "verticals",
    keywords: ["plot", "plots", "floor", "floors", "tower", "apartment", "commercial", "farmland", "villa", "warehouse", "industrial", "vertical"],
    reply: "We've marketed every Indian property vertical — residential plots, high-rise apartments, builder floors, commercial offices, second homes & villas, managed farmland, industrial warehousing and freehold land parcels. Which one is yours?",
  },
  {
    name: "process",
    keywords: ["process", "how do you work", "steps", "approach", "methodology", "flow"],
    reply: "Our 7-step playbook: Discover → Strategize → Create → Launch → Generate Leads → Optimize → Scale. The funnel goes live in 7 to 10 days with weekly war-rooms once it's running.",
  },
  {
    name: "video",
    keywords: ["video", "drone", "3d", "walkthrough", "shoot", "film", "cinematic", "reel"],
    reply: "We have an in-house drone, 3D and cinematic crew — drone flyovers, AI-powered 3D walkthroughs, lifestyle films and UGC-style reels. We send a crew anywhere in India within 72 hours.",
  },
  {
    name: "results",
    keywords: ["results", "case study", "case studies", "case-study", "track record", "experience", "portfolio", "client"],
    reply: "We've worked with 50+ builders, developers and channel partners and delivered 5 lakh+ qualified buyer leads. Want me to share specific case studies from your micro-market?",
  },
  {
    name: "growthcall",
    keywords: ["book", "call", "growth call", "meeting", "consult", "consultation", "audit"],
    reply: "Happy to set you up! Tap the Book a Growth Call button or message us directly on WhatsApp and we'll find a slot within 24 hours. The first audit is free.",
  },
  {
    name: "channelpartner",
    keywords: ["channel partner", "broker", "agent", "brokerage", "cp"],
    reply: "Yes — we activate channel partners and brokers across Delhi NCR, Mumbai, Bangalore, Tier-2 corridors and the NRI markets with co-branded creatives, broker dashboards, WhatsApp lead routing and clear incentive structures.",
  },
  {
    name: "contact",
    keywords: ["contact", "phone", "number", "whatsapp", "email", "reach", "call you"],
    reply: "You can WhatsApp or call us at +91 98188 25449. We usually reply within 5 minutes during business hours.",
  },
  {
    name: "greeting",
    keywords: ["hi", "hello", "hey", "namaste", "hola", "good morning", "good evening", "good afternoon"],
    reply: "Hi there! I'm Savi, the AI assistant from Digi Savvy. We help builders book flats, floors and plots faster. What kind of project are you marketing today?",
  },
  {
    name: "thanks",
    keywords: ["thanks", "thank you", "ty", "thx", "shukriya", "dhanyavad"],
    reply: "Anytime! If you'd like a deeper conversation, tap the WhatsApp button — a real human will reply within minutes.",
  },
];

function normalize(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
}

export function getLocalReply(userMessage) {
  const q = normalize(userMessage);
  if (!q) return "Could you say that again? I'd love to help.";

  // Score each intent by number of keyword hits, prefer longer keyword matches.
  let best = null;
  let bestScore = 0;
  for (const intent of INTENTS) {
    let score = 0;
    for (const kw of intent.keywords) {
      const k = kw.toLowerCase();
      if (q === k) { score += 6; continue; }
      if (q.includes(` ${k} `) || q.startsWith(`${k} `) || q.endsWith(` ${k}`)) score += 3;
      else if (q.includes(k)) score += 2;
    }
    if (score > bestScore) {
      bestScore = score;
      best = intent;
    }
  }

  if (best && bestScore >= 2) return best.reply;

  // Fallback — friendly WhatsApp handoff
  return "Great question! Let me connect you with a real human on our team — they'll reply within minutes. Tap the WhatsApp button or message us at +91 98188 25449.";
}

export const WHATSAPP_FALLBACK = WA;
